import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { forbidExtraProps } from 'airbnb-prop-types';

import { useNavigate } from 'react-router';

import { connect, useDispatch } from 'react-redux';

import { MedicalModal } from '../Components/MedicalModal';

import { isFetchError } from 'Services/HTTPError';

import { useSkincareConcernsOrderAbTest } from 'abTesting/skincareConcernsOrder/hook';

import { productsCategories } from 'constants/products';
import { FIRST_HAIRCARE_QUESTION_ROUTE, FIRST_SKINCARE_QUESTION_ROUTE } from '../constants';
import * as consultationComponents from '../constants/consultationComponents';

import ErrorContainer from 'Containers/ErrorContainer';

import consultationCategoryProp from 'PropTypes/consultationProps';
import consultationSubsetCategoryProp from 'PropTypes/consultationSubsetProps';

import ConsultationLoading from '../Scenes/ConsultationLoading';
import Goals from '../Scenes/Goals';
import Question from '../Scenes/Question';
import SkincareDiet from '../Scenes/SkincareDiet';
import StartStylingGelInterstitial from '../Scenes/StartStylingGelInterstitial';
import Transition from '../Scenes/Transition';

import { getIsAuthenticated } from 'dux/auth/selectors';
import * as consultationSelectors from 'dux/consultation/selectors';
import { initializeConsultation, onNext as onNextThunk } from 'dux/consultation/thunks';

import { useConsultationNotAccessible } from '../hooks/useConsultationNotAccessible';
import { useConsultationStepIsStandalone } from '../hooks/useConsultationStepIsStandalone';
import useConsultationTracking from '../hooks/useConsultationTracking';
import ConsultationSignup from './ConsultationSignup';
import FragranceContainer from './FragranceContainer';
import GeoAggressorsContainer from './GeoAggressorsContainer';
import HeroContainer from './HeroContainer';
import { RoutineProducts } from './RoutineProducts';
import SkincareFragrance from './SkincareFragrance';
import SkincareTextures from './SkincareTextures';
import ZipCodeContainer from './ZipCodeContainer';

const QuestionContainerForConstant = {
  [consultationComponents.DefaultQuestionContainer]: Question,
  [consultationComponents.FragranceContainer]: FragranceContainer,
  [consultationComponents.GeoAggressorsContainer]: GeoAggressorsContainer,
  [consultationComponents.Goals]: Goals,
  [consultationComponents.SignIn]: ConsultationSignup,
  [consultationComponents.Transition]: Transition,
  [consultationComponents.Welcome]: HeroContainer,
  [consultationComponents.ZipCodeContainer]: ZipCodeContainer,
  [consultationComponents.StartStylingGelInterstitial]: StartStylingGelInterstitial,
  [consultationComponents.RoutineProducts]: RoutineProducts,
  [consultationComponents.SkincareFragrance]: SkincareFragrance,
  [consultationComponents.SkincareDiet]: SkincareDiet,
  [consultationComponents.SkincareTextures]: SkincareTextures,
};

const ConstantToModal = {
  [consultationComponents.MedicalModal]: MedicalModal,
};

/**
 * Logic to apply when the question contains a modal.
 * In this case we intercept onNext call and display the modal instead.
 * Once the user click on the modal button the original onNext call is performed
 * with the parameters it was supposed to have.
 *
 * If the Q doesn't have a modal then the regular onNext call is performed directly
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy/Proxy/apply
 * for more informations about intercepting a function call
 */
const useModalProxy = ({ hasModal, onNextFn, requiredOptionsForModalToOpen }) => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const pendingValue = useRef(null);

  let proxiedOnNext;
  if (hasModal) {
    proxiedOnNext = new Proxy(onNextFn, {
      apply(target, _this, args) {
        if (isModalOpened) {
          setIsModalOpened(false);
          return target(...pendingValue.current);
        }

        if (args[0]?.some(option => requiredOptionsForModalToOpen?.includes(option))) {
          pendingValue.current = args;
          setIsModalOpened(true);
        } else {
          return target(...args);
        }

        return undefined;
      },
    });
  }
  const onCloseModal = () => {
    pendingValue.current = null;
    setIsModalOpened(false);
  };

  return { isModalOpened, onCloseModal, onNextCb: hasModal ? proxiedOnNext : onNextFn };
};

const QuestionContainer = ({
  answers,
  consultationCategory,
  consultationSubsetCategory,
  error,
  onNext,
  initialValue,
  isAuthenticated,
  isSaving,
  question,
  questionIndex,
  questionSet,
  firstHaircareConsultationQuestionRoute,
  routeParams,
}) => {
  const cannotAccessConsultationQuestion = useConsultationNotAccessible();
  const isStandalone = useConsultationStepIsStandalone();
  useConsultationTracking({ questionIndex, questionName: question.name });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isModalOpened, onCloseModal, onNextCb } = useModalProxy({
    hasModal: Boolean(question.ModalComponent),
    onNextFn: async (...args) => {
      const { nextRoute, options } = await onNext(...args);
      navigate(nextRoute, options);
    },
    requiredOptionsForModalToOpen: question.requiredOptionsForModalToOpen,
  });
  // trigger abtest tracking
  useSkincareConcernsOrderAbTest({
    isTrackingOnMount: question.name === 'concerns',
  });

  // error saving is simply ignored so user can retry
  const fetchErrorSaving = error && answers && isFetchError(error);

  if (error && !fetchErrorSaving) {
    return (
      <ErrorContainer
        error={error}
        retry={() => dispatch(initializeConsultation())}
        sentryMessage="Invalid state on QuestionContainer"
      />
    );
  }
  if (isAuthenticated && !answers) {
    return <ConsultationLoading />;
  }

  if (cannotAccessConsultationQuestion) {
    const firstConsultationQuestionRoute =
      consultationCategory === productsCategories.SKINCARE
        ? FIRST_SKINCARE_QUESTION_ROUTE
        : firstHaircareConsultationQuestionRoute;

    return (
      <ErrorContainer
        cta="Please resume the consultation"
        recover={() => navigate(firstConsultationQuestionRoute)}
        sentryMessage="unauthenticated user trying to access random consultation question"
        title="We couldn't load your hair profile"
      />
    );
  }

  const Component = QuestionContainerForConstant[question.Component] ?? Question;
  const ModalComponent = ConstantToModal[question.ModalComponent] ?? null;

  return (
    <>
      {ModalComponent && (
        <ModalComponent isOpen={isModalOpened} onClose={onCloseModal} onConfirm={onNextCb} />
      )}
      <Component
        key={question.name}
        answers={answers}
        consultationCategory={consultationCategory}
        consultationSubsetCategory={consultationSubsetCategory}
        cta={isStandalone ? 'save' : 'next'}
        initialValue={initialValue}
        isSaving={isSaving}
        onNext={onNextCb}
        questionIndex={questionIndex}
        questionSet={questionSet}
        routeParams={routeParams}
        standalone={isStandalone}
      />
    </>
  );
};

QuestionContainer.propTypes = forbidExtraProps({
  answers: PropTypes.shape({}),
  consultationCategory: consultationCategoryProp.isRequired,
  consultationSubsetCategory: consultationSubsetCategoryProp,
  isAuthenticated: PropTypes.bool,
  error: PropTypes.shape({}),
  initialValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  isSaving: PropTypes.bool.isRequired,
  questionIndex: PropTypes.number.isRequired,
  question: PropTypes.shape({
    name: PropTypes.string.isRequired,
    Component: PropTypes.oneOf(Object.values(consultationComponents)),
    ModalComponent: PropTypes.oneOf([consultationComponents.MedicalModal]),
    public: PropTypes.bool,
    route: PropTypes.string.isRequired,
    requiredOptionsForModalToOpen: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onNext: PropTypes.func.isRequired,
  questionSet: PropTypes.arrayOf(PropTypes.shape({})),
  firstHaircareConsultationQuestionRoute: PropTypes.string,
  routeParams: PropTypes.shape({}),
});

QuestionContainer.defaultProps = {
  answers: null,
  consultationSubsetCategory: null,
  error: null,
  initialValue: null,
  isAuthenticated: false,
  questionSet: null,
  firstHaircareConsultationQuestionRoute: FIRST_HAIRCARE_QUESTION_ROUTE,
  routeParams: null,
};

export default connect(
  (state, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => ({
    answers: consultationSelectors.getConsultationAnswers(state),
    error: consultationSelectors.getConsultationError(state),
    initialValue: consultationSelectors.getInitialValue(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
    isAuthenticated: getIsAuthenticated(state),
    isSaving: consultationSelectors.getIsSavingAnswer(state),
    question: consultationSelectors.getConsultationQuestionForIndex(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
    questionSet: consultationSelectors.getCurrentlyLoadedHairQuestionSet(state, {
      consultationCategory,
      consultationSubsetCategory,
      routeParams,
    }),
    firstHaircareConsultationQuestionRoute:
      consultationSelectors.getFirstHaircareConsultationQuestionRoute(state),
  }),
  (dispatch, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => ({
    onNext: selected =>
      dispatch(
        onNextThunk({
          consultationCategory,
          consultationSubsetCategory,
          currentQuestionIndex: questionIndex,
          routeParams,
          selected,
        })
      ),
  })
)(QuestionContainer);
